import React from "react";
import { Button, CircularProgress, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { pdf } from "@react-pdf/renderer";
import { api } from "../../../../../api/api";
import { uploadFile } from "../../../../../components/FileUpload/utils/uploadFile";
import { withS3Prefix } from "../../../../../helpers/withS3Prefix";
import { useAppConfig } from "../../../../../security/ApplicationProvider/ApplicationProvider";
import { useDispatchWrapper } from "../../../../../hooks";
import { showMessage } from "../../../../../redux/messages/messagesActions";
import {
  ChooseWhatsAppDIalog,
  sendGenericWhatsAppTemplateAsync,
} from "../../../../../components/ShareViaWhatsApp";
import { IWAServiceProvider } from "../../../../../components/ShareViaWhatsApp/ShareViaWhatsApp.types";
import { useAuth } from "../../../../../contexts/AuthProvider";
import { useBillingCompanyContext } from "../../../../../providers";
import { OutletQuotationDocument } from "../dialogs/RoughEstimatePreviewV2/OutletQuotationDocument";
import { QuotationDocument } from "../dialogs/RoughEstimatePreviewV2/QuotationDocument";

// ─── shared image-to-base64 helper ───────────────────────────────────────────
const imageToBase64 = (url: string): Promise<string> =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve("");
      }
    };
    img.onerror = () => resolve("");
    img.src = url;
  });

// ─── reusable hook ────────────────────────────────────────────────────────────
export const useShareV2 = () => {
  const [sharing, setSharing] = React.useState(false);
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const { logo: appLogo, companyName: appName } = useAppConfig();

  const handleShareV2 = async (leadUuid: string, platform?: IWAServiceProvider) => {
    if (!leadUuid) return;
    setSharing(true);
    try {
      // 1. Fetch payload
      const res = await api.get(
        `/leads/rough-estimate/${leadUuid}/PAYLOAD?multiple_events=1`,
      );
      const payloadData = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];
      const templateFile =
        res.data?.templateFile ||
        res.data?.template_file ||
        res.data?.template ||
        null;

      if (!payloadData || payloadData.length === 0)
        throw new Error("No payload data");

      // 2. Pre-fetch images as base64
      const keys = new Set<string>();
      if (appLogo) keys.add(appLogo);
      payloadData.forEach((item: any) => {
        if (item.logo) keys.add(item.logo);
        if (item.gallery) {
          try {
            const gallery = typeof item.gallery === "string" ? JSON.parse(item.gallery || "[]") : item.gallery;
            if (Array.isArray(gallery)) gallery.forEach((img: string) => { if (img) keys.add(img); });
          } catch { }
        }
        if (item.payment_qr) {
          try {
            const qr = typeof item.payment_qr === "string" ? JSON.parse(item.payment_qr || "[]") : item.payment_qr;
            if (Array.isArray(qr) && qr[0]) {
              const qrKey = typeof qr[0] === "string" ? qr[0] : qr[0]?.path;
              if (qrKey) keys.add(qrKey);
            }
          } catch { }
        }
      });
      const imageCache: Record<string, string> = {};
      await Promise.all(
        Array.from(keys).map(async (key) => {
          const fullUrl = withS3Prefix(key);
          const b64 = await imageToBase64(fullUrl);
          if (b64) imageCache[fullUrl] = b64;
        }),
      );

      // 3. Generate PDF blob
      const isZoya = templateFile
        ? templateFile.trim().toUpperCase().includes("ZOYA")
        : false;
      const doc = isZoya ? (
        <OutletQuotationDocument
          data={payloadData}
          imageCache={imageCache}
          appLogo={appLogo}
          appName={appName}
        />
      ) : (
        <QuotationDocument
          data={payloadData}
          imageCache={imageCache}
          appLogo={appLogo}
          appName={appName}
        />
      );
      const blob = await pdf(doc).toBlob();

      // 4. Upload PDF
      const fileName = `Quotation-${payloadData[0]?.estimate_number || leadUuid}.pdf`;
      const pdfFile = new File([blob], fileName, { type: "application/pdf" });
      const uploadedPath = await uploadFile({
        file: pdfFile,
        module_name: "ROUGH_ESTIMATION",
        defaultReturn: "",
        asPayload: { title: leadUuid },
      });

      if (!uploadedPath) throw new Error("Upload failed");

      // 5. Send WhatsApp via Generic Template API
      const companyOutletUuid =
        payloadData[0]?.company_outlet_uuid ||
        companyOutlet?.comapny?.company_outlet_uuid;
      const rawPhone = (payloadData[0]?.contact_number_one || "").replace(/\D/g, "");
      const countryCode = (payloadData[0]?.country_code || "91").replace(/\D/g, "") || "91";
      const toNumber = rawPhone.startsWith(countryCode) ? rawPhone : `${countryCode}${rawPhone}`;

      await sendGenericWhatsAppTemplateAsync({
        company_outlet_uuid: companyOutletUuid,
        ...(platform ? { platform } : {}),
        env_key: "GENERIC_QUOTATION_WA",
        toNumber,
        payload: {
          name_of_guest: payloadData[0]?.name_of_guest || "",
          rough_estimation_pdf: withS3Prefix(uploadedPath),
        },
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Quotation sent via WhatsApp successfully",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to share quotation",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setSharing(false);
    }
  };

  return { sharing, handleShareV2 };
};

// ─── button component ─────────────────────────────────────────────────────────
interface IShareV2ButtonProps {
  leadUuid: string | null | undefined;
  disabled?: boolean;
}

export const ShareV2Button: React.FC<IShareV2ButtonProps> = ({
  leadUuid,
  disabled = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { sharing, handleShareV2 } = useShareV2();
  const { whatsappService } = useAuth();

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const hasAnyProvider = whatsappService.serviceProviders.length > 0;
  const isDisabled = disabled || sharing || !leadUuid || !hasAnyProvider;

  const resolvedTooltip = !leadUuid
    ? "Save data to send via WhatsApp"
    : !hasAnyProvider
      ? "Configure WhatsApp QR or Meta WhatsApp to send messages."
      : "Share quotation via WhatsApp";

  const handleClick = () => {
    if (isDisabled) return;
    // Multiple providers configured → let the user choose META vs BAILEY.
    if (whatsappService.isMultiple) {
      setDialogOpen(true);
    } else {
      handleShareV2(leadUuid!, whatsappService.selectedService ?? undefined);
    }
  };

  const handleDialogSelect = (provider: IWAServiceProvider) => {
    setDialogOpen(false);
    handleShareV2(leadUuid!, provider);
  };

  return (
    <>
      <Tooltip title={resolvedTooltip} arrow>
        <span>
          <Button
            disabled={isDisabled}
            variant="contained"
            size={isMobile ? "small" : "medium"}
          sx={{
        fontWeight: 600,
        whiteSpace: "nowrap",
        fontSize: "0.8rem",
        py: "4px",
        px: 1.5,
        bgcolor: "#25D366",
        "&:hover": { bgcolor: "#1ebe5d" },
        "&.Mui-disabled": { bgcolor: "action.disabledBackground" }
      }}
            onClick={handleClick}
          >
            {sharing ? (
              <CircularProgress size={14} sx={{ color: "#fff", mr: 0.5 }} />
            ) : (
              <WhatsAppIcon sx={{ fontSize: 18, mr: 0.5 }} />
            )}
            {sharing ? (isMobile ? "..." : "Sending...") : isMobile ? "Share" : "Send Whatsapp"}
          </Button>
        </span>
      </Tooltip>

      <ChooseWhatsAppDIalog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={handleDialogSelect}
        providers={whatsappService.serviceProviders}
      />
    </>
  );
};
